import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../config/api";

const SESSION_KEY = "season_reg_banner_dismissed";

export default function SeasonRegistrationBanner() {
  const [open, setOpen] = useState(false);
  const [seasonName, setSeasonName] = useState("");
  const [isMidSeasonOnly, setIsMidSeasonOnly] = useState(false);
  const navigate = useNavigate();

  // Pre-compute confetti positions so they don't change on re-render
  const confettiPieces = useMemo(
    () =>
      [...Array(40)].map(() => ({
        left: `${Math.random() * 100}%`,
        color: ["#FFD700", "#FF6B6B", "#4ECDC4", "#95E1D3", "#F38181", "#A78BFA", "#34D399"][
          Math.floor(Math.random() * 7)
        ],
        delay: `${Math.random() * 2}s`,
        duration: `${2 + Math.random() * 3}s`,
        rotation: 360 + Math.random() * 360,
        shape: Math.random() > 0.5 ? "circle" : "rect",
      })),
    []
  );

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const check = async () => {
      try {
        const res = await apiRequest("/api/season-registration/status");
        if (res.ok) {
          const data = await res.json();
          if ((data.isOpen || data.midSeasonIsOpen) && data.showPopup !== false) {
            setSeasonName(data.seasonName || "Next Season");
            setIsMidSeasonOnly(!data.isOpen && data.midSeasonIsOpen);
            setOpen(true);
          }
        }
      } catch {
        // silently ignore
      }
    };
    check();
  }, []);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleRegister = () => {
    handleClose();
    navigate("/register");
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          position: "relative",
          overflow: "hidden",
          mx: { xs: 1, sm: 'auto' },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "rgba(255,255,255,0.8)",
          zIndex: 10,
          "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.15)" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Confetti */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 1,
          overflow: "hidden",
        }}
      >
        {confettiPieces.map((piece, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: "-12px",
              left: piece.left,
              width: piece.shape === "circle" ? "8px" : "6px",
              height: piece.shape === "circle" ? "8px" : "12px",
              borderRadius: piece.shape === "circle" ? "50%" : "2px",
              backgroundColor: piece.color,
              animation: `confettiFall ${piece.duration} linear infinite`,
              animationDelay: piece.delay,
              "@keyframes confettiFall": {
                "0%": { transform: "translateY(0) rotate(0deg)", opacity: 1 },
                "100%": {
                  transform: `translateY(120vh) rotate(${piece.rotation}deg)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}
      </Box>

      <DialogContent
        sx={{
          textAlign: "center",
          py: { xs: 4, sm: 6 },
          px: { xs: 2, sm: 4 },
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Emoji header */}
        <Typography
          sx={{
            fontSize: { xs: "3rem", sm: "4rem" },
            lineHeight: 1,
            mb: 2,
            animation: "bounce 0.8s ease-in-out",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-15px)" },
            },
          }}
        >
          🎾🔥
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            mb: 1,
            textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            fontSize: { xs: "1.5rem", sm: "2.125rem" },
          }}
        >
          {isMidSeasonOnly ? "Mid-Season Registration is OPEN!" : "Registration is OPEN!"}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mb: 3,
            opacity: 0.9,
            fontWeight: 400,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          {isMidSeasonOnly
            ? `Join ${seasonName} now — jump into the remaining half!`
            : `${seasonName} is coming — secure your spot now!`}
        </Typography>

        <Box
          sx={{
            display: "inline-block",
            p: 2,
            px: 3,
            mb: 3,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(4px)",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            ⏳ Spots are limited — don't miss out!
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleRegister}
            sx={{
              bgcolor: "white",
              color: "#764ba2",
              fontWeight: 800,
              fontSize: { xs: "1rem", sm: "1.15rem" },
              px: { xs: 4, sm: 6 },
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
              "&:hover": {
                bgcolor: "#f0f0f0",
                transform: "scale(1.03)",
              },
              transition: "all 0.2s",
            }}
          >
            Register Now 🚀
          </Button>
          <Button
            onClick={handleClose}
            sx={{
              color: "rgba(255,255,255,0.7)",
              textTransform: "none",
              fontSize: "0.85rem",
              "&:hover": { color: "white", bgcolor: "transparent" },
            }}
          >
            Maybe later
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
